// Topbar (48 px, gehört zur Arbeitsfläche): links Brotkrumen, rechts die drei globalen Werkzeuge Scannen,
// Benachrichtigungen, KI-Assistent – mehr steht dort nicht (design-system §3.1).
import { Menu, PanelLeftClose, PanelLeftOpen, ScanBarcode } from 'lucide-react'
import { cn } from '../lib/cn'
import { shell, useShellStore } from '../stores/shell'
import { Button } from '../ui/Button'
import { IconButton } from '../ui/IconButton'
import { Kbd } from '../ui/Kbd'
import { Logo } from '../ui/Logo'
import { Breadcrumbs } from '../ui/PageHeader'
import { useAssistantUnavailable } from './AssistantDock'
import type { Crumb } from './nav'
import { NotificationBell } from './NotificationCenter'

export const AssistantMark = ({ className }: { className?: string }) => (
  <Logo variant="bare" size={16} title={null} className={className} />
)

export interface TopbarProps {
  crumbs: Crumb[]
  /** die Sidebar ist gerade eine Schiene (vom Nutzer oder vom Platz) */
  sidebarCollapsed: boolean
  /**
   * 'collapse': ein- und ausklappen (Desktop, ⌘B) · 'overlay': auf Tablet-Breite ist die Schiene fest – die Schaltfläche
   * öffnet die volle Navigation als überlagernde Seitenleiste (docs/mobile/SPEZIFIKATION.md §6.4)
   */
  sidebarToggle: 'collapse' | 'overlay'
}

export function Topbar({ crumbs, sidebarCollapsed, sidebarToggle }: TopbarProps) {
  const navOpen = useShellStore((state) => state.mobileNavOpen)
  const assistantOpen = useShellStore((state) => state.assistantOpen)
  // steht der Assistent nicht zur Verfügung, bleibt die Schaltfläche da (das Panel sagt, warum) – nur gedämpft
  const assistantOff = useAssistantUnavailable()
  return (
    <div
      className="flex h-(--topbar-h) shrink-0 items-center gap-2 border-b border-line pr-3 pl-2"
      data-print="hide"
      data-testid="topbar"
    >
      {sidebarToggle === 'overlay' ? (
        <IconButton
          icon={Menu}
          label="Navigation öffnen"
          hotkey="mod+b"
          size="sm"
          tooltipSide="bottom"
          aria-expanded={navOpen}
          onClick={() => shell.setMobileNav(true)}
          className="text-fg-muted"
          data-testid="nav-overlay-open"
        />
      ) : (
        <IconButton
          icon={sidebarCollapsed ? PanelLeftOpen : PanelLeftClose}
          label={sidebarCollapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'}
          hotkey="mod+b"
          size="sm"
          tooltipSide="bottom"
          onClick={shell.toggleSidebar}
          className="text-fg-subtle"
        />
      )}
      <Breadcrumbs items={crumbs} className="flex-1" />
      <Button variant="ghost" icon={ScanBarcode} className="max-lg:hidden" onClick={() => shell.setScan(true)}>
        Scannen
      </Button>
      <IconButton icon={ScanBarcode} label="Scannen" className="lg:hidden" onClick={() => shell.setScan(true)} />
      <NotificationBell />
      <Button
        aria-pressed={assistantOpen}
        onClick={shell.toggleAssistant}
        className={cn(
          'max-lg:hidden',
          assistantOpen && 'bg-pressed',
          assistantOff && 'border-line text-fg-muted shadow-none',
        )}
        data-testid="assistant-toggle"
        data-unavailable={assistantOff || undefined}
      >
        <span className="flex items-center gap-2">
          <Logo variant="bare" size={14} title={null} />
          KI-Assistent
          <Kbd hotkey="mod+j" variant="plain" className="text-fg-subtle touch:hidden" />
        </span>
      </Button>
      <IconButton
        icon={AssistantMark}
        label="KI-Assistent"
        hotkey="mod+j"
        pressed={assistantOpen}
        className={cn('lg:hidden', assistantOff && 'text-fg-subtle')}
        onClick={shell.toggleAssistant}
      />
    </div>
  )
}
